import { inject, readonly, ref } from 'vue'
import type {
  AccessRoleWithPermissions,
  AccessCode,
  UserInRole
} from '@gat-ship/types'
import {
  accessRoleDetailKey,
  accessRolesCollectionKey,
  type AccessRoleDetailService,
  type AccessRolesCollectionService
} from '@gat-ship/types/injection-keys'
import { useAccessControlSignalREvents } from '@gat-ship/signalr'
import { AccessRoleDataBuilder } from '../model/access-role-data-builder'
import { containsAccessRoleId } from '../model/access-role-collection'
import { useAccessControlApiCalls } from './useAccessControlApiCalls'
import { useAccessRolesCollection } from './useAccessRolesCollection'

/**
 * Data source for a single access role's detail
 * (`AccessRoleWithPermissions` + the users assigned to it).
 *
 * Page-scoped to the role permissions view. Depends on the collection
 * data source ({@link useAccessRolesCollection}) to keep the shared list
 * in sync on name changes.
 */
export function useAccessRoleDetail(
  accessRolesCollection?: AccessRolesCollectionService
): AccessRoleDetailService {
  const {
    addRolePermissionsRequest,
    changeAccessRoleNameRequest,
    getAccessRoleRequest,
    getUsersByRoleRequest,
    removeRolePermissionsRequest
  } = useAccessControlApiCalls()
  // Resolve the SHARED collection (the one holding the fetched roles) so
  // `changeAccessRoleName` can delegate to it. Prefer an explicit arg, then the
  // collection provided under `accessRolesCollectionKey` by an ancestor
  // provider; only spin up a standalone collection when neither exists.
  const _accessRolesCollection =
    accessRolesCollection ??
    inject(accessRolesCollectionKey, undefined) ??
    useAccessRolesCollection()

  const _accessRole = ref<AccessRoleWithPermissions>()

  const _usersWithAccessRole = ref<UserInRole[]>([])

  function _setUsersWithAccessRole(updatedUsersWithAccessRole: UserInRole[]) {
    _usersWithAccessRole.value = updatedUsersWithAccessRole
  }

  function _setAccessRole(updatedAccessRole: AccessRoleWithPermissions) {
    _accessRole.value = updatedAccessRole
  }

  async function changeAccessRoleName(
    roleId: string,
    name: string,
    description: string
  ) {
    // Delegate to the shared collection only when it actually holds this role,
    // so the list stays in sync. When the detail modal is opened standalone
    // (e.g. deep-linked `?modals=access-role:<id>`) the collection is empty /
    // unfetched and its `changeAccessRoleName` would silently no-op — fall
    // through to the direct request off the loaded single role instead.
    const collectionHasRole =
      _accessRolesCollection !== undefined &&
      containsAccessRoleId(_accessRolesCollection.accessRoles.value, roleId)
    if (_accessRolesCollection && collectionHasRole) {
      const result = await _accessRolesCollection.changeAccessRoleName(
        roleId,
        name,
        description
      )
      if (result) {
        _setAccessRole(
          AccessRoleDataBuilder.copyAccessRoleWithPermissions({
            id: result.id,
            name: result.name,
            description: result.description,
            permissionCodes: _accessRole.value?.permissionCodes ?? []
          })
        )
      }
      return result
    }
    if (_accessRole.value?.id === roleId) {
      const orgAccessRole = AccessRoleDataBuilder.copyAccessRoleWithPermissions(
        _accessRole.value
      )
      const updatedAccessRole =
        AccessRoleDataBuilder.changeNameOfAccessRoleWithPermissions(
          _accessRole.value,
          name,
          description
        )
      try {
        _setAccessRole(updatedAccessRole)
        const response = await changeAccessRoleNameRequest({
          roleId,
          payload: {
            name,
            description
          },
          commandId: 'change-access-role-name'
        })
        return response
      } catch (error) {
        _setAccessRole(orgAccessRole)
        throw error
      }
    }
    return undefined
  }

  async function addRolePermissions(permissionCodes: AccessCode[]) {
    const roleId = _accessRole.value?.id
    const accessRole = _accessRole.value
    if (roleId && accessRole) {
      const orgAccessRole =
        AccessRoleDataBuilder.copyAccessRoleWithPermissions(accessRole)
      const updatedAccessRole = AccessRoleDataBuilder.addRolePermissions(
        accessRole,
        permissionCodes
      )
      try {
        _setAccessRole(updatedAccessRole)
        const response = await addRolePermissionsRequest({
          roleId,
          payload: {
            permissionCodes
          },
          commandId: 'add-role-permissions'
        })
        return response
      } catch (error) {
        _setAccessRole(orgAccessRole)
        throw error
      }
    }
  }

  async function removeRolePermissions(permissionCodes: string[]) {
    const roleId = _accessRole.value?.id
    const accessRole = _accessRole.value
    if (roleId && accessRole) {
      const orgAccessRole =
        AccessRoleDataBuilder.copyAccessRoleWithPermissions(accessRole)
      const updatedAccessRole = AccessRoleDataBuilder.removeRolePermissions(
        accessRole,
        permissionCodes
      )
      try {
        _setAccessRole(updatedAccessRole)
        const response = await removeRolePermissionsRequest({
          roleId,
          payload: {
            permissionCodes
          },
          commandId: 'remove-role-permissions'
        })
        return response
      } catch (error) {
        _setAccessRole(orgAccessRole)
        throw error
      }
    }
  }
  const signalREvents = useAccessControlSignalREvents({
    onAccessRoleDeleted: () => {
      _accessRole.value = undefined
    },
    onAccessRoleNameChanged: async (payload) => {
      const { roleId } = payload.message
      if (roleId === _accessRole.value?.id) {
        const result = await getAccessRoleRequest({ roleId })
        _setAccessRole(result.role)
      }
    }
  })

  /**
   * Fetches the access role. If the access role is already loaded, it will not be fetched again unless forceFetch is true.
   * @param options.forceFetch - Whether to force a fetch of the access role. @default false
   * @param options.subscribeToEvents - Whether to subscribe to access role events. @default true
   */
  async function fetchAccessRole(
    roleId: string,
    options?: {
      forceFetch?: boolean
      subscribeToEvents?: boolean
    }
  ) {
    if (
      _accessRole.value === undefined ||
      options?.forceFetch ||
      roleId !== _accessRole.value?.id
    ) {
      await fetchUsersWithAccessRole(roleId)
      const result = await getAccessRoleRequest({
        roleId
      })
      _setAccessRole(result.role)

      const subscribeToEvents = options?.subscribeToEvents ?? true
      if (subscribeToEvents) {
        signalREvents.subscribeToAccessControlEvents()
      }

      return result
    }
    return undefined
  }

  async function fetchUsersWithAccessRole(roleId: string) {
    const result = await getUsersByRoleRequest({
      roleId
    })
    _setUsersWithAccessRole(result.users)
  }

  // Bound to a local before returning so the extra `signalREvents` member
  // (a `@gat-ship/signalr` value-type, intentionally absent from the
  // runtime-free `AccessRoleDetailService` contract) is allowed through
  // structural assignability instead of tripping excess-property checks.
  const service = {
    accessRole: _accessRole,
    usersWithAccessRole: readonly(_usersWithAccessRole),
    addRolePermissions,
    removeRolePermissions,
    changeAccessRoleName,
    clearData: (options?: { unsubscribeFromEvents?: boolean }) => {
      _accessRole.value = undefined
      if (options?.unsubscribeFromEvents) {
        signalREvents.unsubscribeFromAccessControlEvents()
      }
    },
    fetchAccessRole,
    fetchUsersWithAccessRole,
    signalREvents
  }
  return service
}

/** Inject the access-role detail context provided by a parent provider. */
export function useAccessRoleDetailContext() {
  return inject(accessRoleDetailKey, undefined)
}
